import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import api from '../../../../api';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { FaListAlt } from 'react-icons/fa';
import { XMarkIcon } from '@heroicons/react/24/outline';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 1000,
    easing: 'easeOutBack',
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#fff',
      titleColor: '#111827',
      bodyColor: '#1F2937',
      borderColor: '#E5E7EB',
      borderWidth: 1,
      padding: 10,
      cornerRadius: 8,
      callbacks: {
        label: (ctx) => `Total Duration: ${ctx.raw} mins`,
      },
    },
  },
  scales: {
    x: {
      ticks: { color: '#1A2B5C', font: { size: 13, weight: '600' } },
      grid: { display: false },
    },
    y: {
      ticks: { color: '#1A2B5C' },
      grid: { color: '#E2E8F0' },
    },
  },
};

const CardWrapper = ({ children }) => (
  <div className="relative w-full max-w-6xl mx-auto p-6 rounded-[2rem] bg-white/80 backdrop-blur-lg border border-slate-200 shadow-[0_15px_40px_rgba(0,0,0,0.1)] overflow-hidden">
    <div className="absolute top-0 left-0 w-full h-[10px] bg-gradient-to-r from-[#EE1D23] via-pink-500 to-[#1A2B5C] rounded-t-[2rem] z-0" />
    <div className="absolute bottom-0 left-10 right-10 h-[6px] bg-gradient-to-r from-[#1A2B5C] to-[#EE1D23] rounded-full blur-md opacity-60 animate-pulse" />
    <div className="relative z-10">{children}</div>
  </div>
);

const NoDataMessage = () => (
  <div className="flex flex-col items-center justify-center h-full text-center animate-fadeIn">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-[#EE1D23]/70 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.75V3m0 18v-1.75M6.364 6.364l-1.061-1.061m12.728 12.728l-1.061-1.061M4.75 12H3m18 0h-1.75M6.364 17.636l-1.061 1.061m12.728-12.728l-1.061 1.061" />
    </svg>
    <div className="text-lg font-semibold text-[#1A2B5C] mb-1">No Data Available</div>
    <p className="text-sm text-gray-500 max-w-sm">
      No MISC reason data found. Try a different filter or check again later.
    </p>
  </div>
);

const MiscReasons = ({ filters }) => {
  const { accessToken } = useAuth();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      try {
        const baseUrl = getRoundRobinBaseUrl();

        const response = await api.get(
          `${baseUrl}/superadminDashboard/getSuperAdminMISCReasonAnalytics`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: filters || {},
          }
        );

        const reasons = Array.isArray(response.data?.data?.currentMonthData)
          ? response.data.data.currentMonthData
          : [];

        const labels = reasons.map((r) => r._id);
        const values = reasons.map((r) => r.Totalduration);
        const barColors = values.map((_, idx) => (idx % 2 === 0 ? '#EE1D23' : '#1A2B5C'));

        const formattedData = {
          labels,
          datasets: [
            {
              label: 'Total Duration (mins)',
              data: values,
              backgroundColor: barColors,
              borderRadius: 20,
              barThickness: 30,
              borderSkipped: false,
            },
          ],
        };

        setChartData(formattedData);
      } catch (error) {
        console.error('Error fetching reason analytics:', error);
        setChartData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [accessToken, filters]);

  const header = (
    <div className="flex items-center gap-4 mb-6">
      <div className="relative">
        <div className="bg-white p-3 rounded-full border border-[#1A2B5C] shadow-xl text-[#1A2B5C] text-2xl">
          <FaListAlt />
        </div>
        <div className="absolute top-0 right-0 w-3 h-3 bg-[#EE1D23] rounded-full animate-ping" />
      </div>
      <h2 className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-[#EE1D23] to-[#1A2B5C] bg-clip-text text-transparent">
        MISC Reason Analytics
      </h2>
      <button
        onClick={() => setFocus(true)}
        className="ml-auto px-4 py-1.5 text-xs font-semibold rounded-md border border-[#1A2B5C] text-[#1A2B5C] hover:bg-[#1A2B5C] hover:text-white transition"
      >
        Focus Mode
      </button>
    </div>
  );

  return (
    <>
      {!focus && (
        <CardWrapper>
          {header}
          <div className="h-[400px] transition-all duration-700">
            {loading ? (
              <div className="flex justify-center items-center h-full text-gray-400">
                Loading chart...
              </div>
            ) : chartData ? (
              <Bar data={chartData} options={chartOptions} />
            ) : (
              <NoDataMessage />
            )}
          </div>
        </CardWrapper>
      )}

      {focus && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <CardWrapper>
            <button
              onClick={() => setFocus(false)}
              className="absolute top-5 right-5 bg-white border border-gray-200 shadow-md rounded-full p-2 text-gray-600 hover:text-[#EE1D23]"
              title="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            {header}
            <div className="h-[60vh]">
              {loading ? (
                <div className="flex justify-center items-center h-full text-gray-400">
                  Loading chart...
                </div>
              ) : chartData ? (
                <Bar data={chartData} options={chartOptions} />
              ) : (
                <NoDataMessage />
              )}
            </div>
          </CardWrapper>
        </div>
      )}
    </>
  );
};

export default MiscReasons;
